/**
 * Modal para Documentos Obligatorios con Firma
 * Componente genérico reutilizable para cualquier documento que requiera firma
 * Se muestra cuando el usuario tiene documentos pendientes obligatorios
 */
import React, { useState, useEffect } from 'react';
import { Modal, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import SignaturePad from './SignaturePad';
import axios from 'axios';
import './DocumentoObligatorioModal.css';

const API_URL = process.env.REACT_APP_API_URL || 
  (process.env.NODE_ENV === 'development' ? 'http://localhost:4005' : '');

const DocumentoObligatorioModal = ({ show, onAccepted, userData }) => {
  const [documento, setDocumento] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Lectura, 2: Firma
  const [firmaData, setFirmaData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      cargarDocumentoPendiente();
    }
  }, [show]);

  const cargarDocumentoPendiente = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/documentos-obligatorios/pendientes`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.documento) {
        setDocumento(response.data.documento);
      } else {
        // No hay documentos pendientes
        setError('No hay documentos pendientes');
        if (onAccepted) {
          onAccepted(); // Cerrar modal
        }
      }
    } catch (err) {
      console.error('Error cargando documento:', err);
      setError('Error al cargar el documento');
    } finally {
      setLoading(false);
    }
  };

  const handleFirmaGuardada = (firmaBase64) => {
    setFirmaData(firmaBase64);
    console.log('✅ Firma capturada');
  };

  const handleFirmaLimpiada = () => {
    setFirmaData(null);
  };

  const handleSubmit = async () => {
    if (!firmaData) {
      alert('Debe firmar el documento para continuar');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      await axios.post(
        `${API_URL}/api/documentos-obligatorios/firmar`,
        {
          documentoId: documento.id,
          firmaBase64: firmaData
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('✅ Firma registrada exitosamente');
      
      // Notificar que se aceptó correctamente
      if (onAccepted) {
        onAccepted();
      }

    } catch (err) {
      console.error('❌ Error registrando firma:', err);
      setError(err.response?.data?.error || 'Error al registrar la firma');
      setSubmitting(false);
    }
  };

  const getPdfUrl = () => {
    if (!documento) return null;
    const token = localStorage.getItem('token');
    return `${API_URL}/api/documentos-obligatorios/pdf/${documento.id}?token=${token}`;
  };

  const getTipoDocumentoBadge = (tipo) => {
    const badges = {
      normativa: { variant: 'danger', text: 'Normativa' },
      procedimiento: { variant: 'primary', text: 'Procedimiento' },
      acuerdo: { variant: 'success', text: 'Acuerdo' },
      politica_interna: { variant: 'warning', text: 'Política Interna' },
      otro: { variant: 'secondary', text: 'Documento' }
    };
    return badges[tipo] || badges.otro;
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      size="xl"
      centered
      className="documento-obligatorio-modal"
    >
      <Modal.Header className="bg-primary text-white">
        <Modal.Title>
          {documento && (
            <>
              📋 {documento.titulo}
              <Badge 
                bg={getTipoDocumentoBadge(documento.tipo_documento).variant} 
                className="ms-3"
              >
                {getTipoDocumentoBadge(documento.tipo_documento).text}
              </Badge>
              <Badge bg="secondary" className="ms-2">v{documento.version}</Badge>
            </>
          )}
          {!documento && '📋 Documento Obligatorio'}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="documento-obligatorio-body">
        {loading && (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Cargando documento...</p>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {!loading && !error && documento && (
          <>
            <Alert variant="info" className="mb-4">
              <strong>⚠️ Lectura y firma obligatoria</strong>
              {documento.descripcion && (
                <p className="mb-0 mt-2">{documento.descripcion}</p>
              )}
              <p className="mb-0 mt-2">
                Este documento es de carácter <strong>obligatorio</strong>. 
                Debe leerlo completamente y firmarlo para poder acceder al sistema.
              </p>
            </Alert>

            {step === 1 && (
              <div className="documento-step">
                <h5 className="mb-3">📄 Paso 1: Lectura del documento</h5>
                <p className="text-muted mb-3">
                  Por favor, lea el documento completo antes de continuar con la firma.
                </p>
                
                <div className="pdf-viewer-container mb-4">
                  <iframe
                    src={getPdfUrl()}
                    width="100%"
                    height="600px"
                    title="Documento PDF"
                    style={{ border: '1px solid #dee2e6', borderRadius: '8px' }}
                  />
                </div>

                <div className="d-flex justify-content-end">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => setStep(2)}
                  >
                    He leído el documento, continuar con la firma →
                  </Button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="firma-step">
                <h5 className="mb-3">✍️ Paso 2: Firma digital</h5>
                <p className="text-muted mb-3">
                  Firme en el recuadro a continuación para confirmar que ha leído y acepta el documento.
                </p>

                <Alert variant="secondary" className="mb-3">
                  <div className="d-flex align-items-center">
                    <div className="me-3">
                      <strong>Usuario:</strong> {userData?.Nombre || userData?.NmEmpleado}
                    </div>
                    <div className="me-3">
                      <strong>Legajo:</strong> {userData?.Legajo}
                    </div>
                    <div>
                      <strong>Fecha:</strong> {new Date().toLocaleDateString('es-AR')}
                    </div>
                  </div>
                </Alert>

                <SignaturePad
                  onSave={handleFirmaGuardada}
                  onClear={handleFirmaLimpiada}
                />

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStep(1)}
                    disabled={submitting}
                  >
                    ← Volver al documento
                  </Button>

                  <Button
                    variant="success"
                    size="lg"
                    onClick={handleSubmit}
                    disabled={!firmaData || submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          className="me-2"
                        />
                        Registrando firma...
                      </>
                    ) : (
                      <>
                        ✓ Confirmar y firmar documento
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default DocumentoObligatorioModal;
